import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useBackgroundColor } from '../../../utils';
import tokens from './tokens';

export interface BaseFormProps {
  id: string;
  invalid?: boolean;
  valid?: boolean;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  defaultValue?: string | number | boolean;
  large?: boolean;
  leftAligned?: boolean;
  centerAligned?: boolean;
  readOnly?: boolean;
  onChange?: (...event: any[]) => void;
}

interface StyledInputProps {
  active?: boolean;
  arrowIcon?: boolean;
  centerAligned?: boolean;
  invalid?: boolean;
  large?: boolean;
  leftAligned?: boolean;
  readOnly?: boolean;
  valid?: boolean;
}

export const StyledBase = styled.input<StyledInputProps>`
  ${({
    theme: { borderRadii, colors, space },
    active,
    arrowIcon,
    as,
    centerAligned,
    invalid,
    large,
    leftAligned,
    readOnly,
    valid,
  }) => css`
    border: solid 1px ${colors[tokens.formFieldBorderColor]};
    ${useBackgroundColor('utilityWhite')}
    padding: ${large
      ? `${space[tokens.formFieldPaddingLarge]}`
      : `${space.xSmall} ${space.small}`};
    width: 100%;
    display: block;
    position: relative;
    transition: ${tokens.transition};
    border-radius: ${borderRadii[tokens.borderRadius]};

    &::placeholder {
      color: ${colors[tokens.placeHolderColor]};
    }

    &:focus,
    &:hover {
      ${tokens.focusBoxShadow
        ? ''
        : `box-shadow: 0 0 0 1px ${colors.brandOne120}`};
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    /* Works for Firefox */
    &[type='number'] {
      -moz-appearance: textfield;
    }

    ${leftAligned &&
    css`
      text-align: left;
    `}

    ${centerAligned &&
    css`
      text-align: center;
    `}

    ${active &&
    css`
      box-shadow: 0 0 0 1px ${colors.brandOne120};
    `}

    ${readOnly &&
    css`
      background-color: ${colors.utilityBackground};
      border-color: transparent;
      caret-color: transparent;
      cursor: pointer;

      &:focus-visible {
        box-shadow: none;
      }
    `}

      // If select input
    ${as === 'select' &&
    css`
      background-image: ${arrowIcon
        ? `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 11 7' class='Icon__StyledIcon-NDAcm ieTwjA'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0 0h32v32H0z'%3E%3C/path%3E%3Cpath fill='currentColor' d='M5.09 6.5L0 1.415L1.415 0L5.09 3.675L8.765 0L10.18 1.415L5.09 6.5Z'%3E%3C/path%3E%3C/g%3E%3C/svg%3E")`
        : `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32' class='Icon__StyledIcon-NDAcm ieTwjA'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cpath d='M0 0h32v32H0z'%3E%3C/path%3E%3Cpath fill='currentColor' d='M8.25 11h16.09c1.112 0 1.669 1.344.88 2.132l-8.04 8.047a1.253 1.253 0 0 1-1.77 0l-8.04-8.047C6.58 12.344 7.137 11 8.25 11Z'%3E%3C/path%3E%3C/g%3E%3C/svg%3E")`};
      background-repeat: no-repeat;
      background-position: right 16px center;
      background-size: 16px 16px;
      appearance: none;
      padding-right: ${space.xLarge};
    `}

    &[role="combobox"] {
      padding-right: ${space.xLarge};
    }

    // Invalid state

    ${invalid &&
    css`
      border-color: ${colors.systemError};
      box-shadow: 0 0 0 1px ${colors.systemError};
      ${tokens.borderCorrection &&
      css`
        padding: ${large
          ? `${parseFloat(space[tokens.formFieldPaddingLarge]) - 0.125}rem`
          : `${parseFloat(space.xSmall) - 0.125}rem ${
              parseFloat(space.small) - 0.125
            }rem`};
      `}
      &:hover {
        box-shadow: 0 0 0 1px ${colors.systemError};
      }

      &:focus-visible {
        ${tokens.focusInvalidBoxShadow
          ? `box-shadow: ${tokens.focusInvalidBoxShadow}`
          : `box-shadow: 0 0 0 1px ${colors.systemError}`};
      }
    `}

    // valid state

    ${valid &&
    css`
      border: ${tokens.borderSize} solid ${colors.systemSuccess};
      box-shadow: 0 0 0 1px ${colors.systemSuccess};
      ${tokens.borderCorrection &&
      css`
        padding: ${large
          ? `${parseFloat(space[tokens.formFieldPaddingLarge]) - 0.125}rem`
          : `${parseFloat(space.xSmall) - 0.125}rem ${
              parseFloat(space.small) - 0.125
            }rem`};
      `}
      &:hover {
        box-shadow: 0 0 0 1px ${colors.systemSuccess};
      }

      &:focus-visible {
        ${tokens.focusValidBoxShadow
          ? `box-shadow: ${tokens.focusValidBoxShadow}`
          : `box-shadow: 0 0 0 1px ${colors.systemSuccess}`};
      }
    `}
  `}
`;
